import { z } from "zod";

export const typographyUsageRoleSchema = z.enum(["headings", "body", "mixed"]);

const stripWrappingQuotes = (value: string): string => value.replace(/^['"]|['"]$/g, "").trim();

const stripNextJsFontArtifacts = (value: string): string => {
  const wrapped = stripWrappingQuotes(value);
  let cleaned = wrapped;
  const likelyNextJsFamily =
    /^_+/.test(wrapped) ||
    /_Fallback(?:_[a-z0-9]+)?$/i.test(wrapped) ||
    /^__.+_[a-z0-9]{5,}$/i.test(wrapped);

  // Example Next.js families:
  // - "__Inter_20b187"
  // - "__Inter_Fallback_20b187"
  if (likelyNextJsFamily) {
    cleaned = cleaned.replace(/^_+/, "");
    cleaned = cleaned.replace(/_Fallback(?:_[a-z0-9]+)?$/i, "");
    cleaned = cleaned.replace(/_[a-z0-9]{5,}$/i, "");
  }

  cleaned = cleaned.replace(/_+/g, " ").replace(/\s+/g, " ").trim();

  return cleaned.length > 0 ? cleaned : wrapped;
};

export const toDisplayFontFamilyName = (value: string): string => stripNextJsFontArtifacts(value);

export const normalizeFontFamilyName = (value: string): string =>
  stripNextJsFontArtifacts(value).toLowerCase();

export const typographyFamilyClaimSchema = z.object({
  family: z.string().min(1),
  normalizedFamily: z.string().min(1).optional(),
  role: typographyUsageRoleSchema,
  usageCount: z.number().int().nonnegative(),
  variants: z.array(z.string()).default([]),
  sourceUrls: z.array(z.string()).default([]),
  assetObjectKeys: z.array(z.string()).default([]),
});

export const typographyStyleClaimSchema = z.object({
  version: z.literal(1).default(1),
  summary: z.string(),
  families: z.array(typographyFamilyClaimSchema),
});

export type TypographyUsageRole = z.infer<typeof typographyUsageRoleSchema>;
export type TypographyFamilyClaim = z.infer<typeof typographyFamilyClaimSchema>;
export type TypographyStyleClaim = z.infer<typeof typographyStyleClaimSchema>;

const TYPOGRAPHY_ROLE_LABEL: Record<TypographyUsageRole, string> = {
  headings: "Headings",
  body: "Body",
  mixed: "Mixed",
};

export const parseTypographyStyleClaim = (value: unknown): TypographyStyleClaim | null => {
  const parsed = typographyStyleClaimSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const formatTypographyFamilySummary = (family: TypographyFamilyClaim): string => {
  const weightText = family.variants.length > 0 ? family.variants.join(", ") : "400";
  return `${family.family} (${TYPOGRAPHY_ROLE_LABEL[family.role]}) - weights: ${weightText}`;
};

export const buildTypographySummary = (families: TypographyFamilyClaim[], maxItems = 5): string => {
  if (families.length === 0) {
    return "No recognized typography setup.";
  }

  return families
    .slice(0, maxItems)
    .map((family) => formatTypographyFamilySummary(family))
    .join(" | ");
};
